import {buildStandardHeaders, otsBaseApi} from "../../ots-api";
import {DVIR, DVIRMechanicReview, DVIRSearchCriteria, PartialDVIR} from "../../../model/dvir";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {OtsAsyncThunkConfigExtra} from "../../ots-store";
import {removeUndefined} from "../../../util/util";
import {adjustToTimeZone, subDays} from "../../../date/date";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["DVIRs", "ObjectDVIRs", "ObjectObjectChangeLogs"]});

const dvirApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchDVIRs: builder.query<
      DVIR[],
      DVIRSearchCriteria
    >({
      query: (criteria) => ({
        url: `/dvirs/search`,
        method: 'POST',
        body: criteria,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DVIRs", id} as const)),
            {type: "DVIRs", id: "LIST"}
          ];
        } else {
          return [{type: "DVIRs", id: "LIST"}];
        }
      },
    }),
    getLatestPersonDVIRs: builder.query<
      DVIR[],
      { personId: string, regionId: string }
    >({
      query: (args) => ({
        url: `/dvirs/search`,
        method: 'POST',
        body: removeUndefined({
          fromDttm: subDays(adjustToTimeZone().getTime(), 7).getTime(),
          region: { scope: "SPECIFIC", values: [args.regionId] },
          person: { scope: "SPECIFIC", values: [args.personId] },
        }),
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, args) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DVIRs", id} as const)),
            {type: "ObjectDVIRs", id: args.personId}
          ];
        } else {
          return [{type: "ObjectDVIRs", id: args.personId}];
        }
      },
    }),
    getLatestVehicleDVIRs: builder.query<
      DVIR[],
      { vehicleId: string, regionId: string }
    >({
      query: (args) => ({
        url: `/dvirs/search`,
        method: 'POST',
        body: removeUndefined({
          fromDttm: subDays(adjustToTimeZone().getTime(), 7).getTime(),
          region: { scope: "SPECIFIC", values: [args.regionId] },
          vehicle: { scope: "SPECIFIC", values: [args.vehicleId] },
        }),
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, args) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DVIRs", id} as const)),
            {type: "ObjectDVIRs", id: args.vehicleId}
          ];
        } else {
          return [{type: "ObjectDVIRs", id: args.vehicleId}];
        }
      },
    }),
    getLatestTrailerDVIRs: builder.query<
      DVIR[],
      { trailerId: string, regionId: string }
    >({
      query: (args) => ({
        url: `/dvirs/search`,
        method: 'POST',
        body: removeUndefined({
          fromDttm: subDays(adjustToTimeZone().getTime(), 7).getTime(),
          region: { scope: "SPECIFIC", values: [args.regionId] },
          trailer: { scope: "SPECIFIC", values: [args.trailerId] },
        }),
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, args) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DVIRs", id} as const)),
            {type: "ObjectDVIRs", id: args.trailerId}
          ];
        } else {
          return [{type: "ObjectDVIRs", id: args.trailerId}];
        }
      },
    }),
    getMechanicReviewableDVIRs: builder.query<
      DVIR[],
      string
    >({
      query: (args) => ({
        url: `/dvirs/mechanics/reviewables`,
        method: 'GET',
        params: removeUndefined({
          regionIds: [args],
        }),
      }),
      keepUnusedDataFor: 120,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DVIRs", id} as const)),
          ];
        } else {
          return []
        }
      },
    }),
    getDVIR: builder.query<DVIR, string>({
      query: (id) => ({
        url: `/dvirs/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "DVIRs", id}]
    }),
    getDVIRBadgeSummary: builder.query<DVIR, string>({
      query: (arg) => ({
        url: `/dvirs/badge-summary`,
        method: "GET",
        params: removeUndefined({
          regionIds: [arg],
        }),
      }),
    }),
    createDVIR: builder.mutation<DVIR, PartialDVIR>({
      query: (mapping) => ({
        url: "/dvirs",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [{type: "DVIRs", id: "LIST"}]
    }),
    updateDVIR: builder.mutation<DVIR, PartialDVIR>({
      query: (mapping) => ({
        url: "/dvirs",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "DVIRs", id},
        {type: "ObjectObjectChangeLogs", id}
      ]
    }),
    deleteDVIR: builder.mutation<void, Partial<DVIR>>({
      query: (mapping) => ({
        url: "/dvirs",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [{type: "DVIRs", id}]
    }),
    updateDVIRMechanicReview: builder.mutation<void, { id: string, review: DVIRMechanicReview }>({
      query: ({id, review}) => ({
        url: `/dvirs/${id}/mechanic-review`,
        method: "PUT",
        body: review,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "DVIRs", id},
        {type: "ObjectObjectChangeLogs", id}
      ]
    }),
  })
});

export const {
  useSearchDVIRsQuery,
  useGetLatestPersonDVIRsQuery,
  useGetLatestVehicleDVIRsQuery,
  useGetLatestTrailerDVIRsQuery,
  useGetMechanicReviewableDVIRsQuery,
  useGetDVIRQuery,
  useGetDVIRBadgeSummaryQuery,
  useCreateDVIRMutation,
  useUpdateDVIRMutation,
  useUpdateDVIRMechanicReviewMutation,
  useDeleteDVIRMutation,
} = dvirApi;

export const downloadDVIR = (id: string, regionId: string | undefined, api: BaseQueryApi): Promise<Response> => {
  const headers = buildStandardHeaders(api)
  headers["Accept"] = 'application/json'
  headers["Content-Type"] = 'application/json'

  const request: RequestInit = {
    method: 'GET',
    headers: headers,
  }

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;

  let url = `${baseUrl}/dvirs/${id}/download`;
  if (regionId) {
    url += `?regionId=${regionId}`
  }

  return window.fetch(url, request)
    .then((response) => {
      if (response.ok) {
        return Promise.resolve(response)
      } else {
        return Promise.reject(response)
      }
    });
}
